import React, { Component } from 'react'
import cx from 'classnames'
import autobind from 'autobind-decorator'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import Button from 'components/Button/Button'
import Icon from 'components/Icon/Icon'
import style from './Map.scss'
import _ from 'lodash'

const fillOpacity = 1

const MARKER_WEIGHT_BASE = 0.05
const MAX_ZOOM = 10

const allowedWidgets = ['zoomLevel'] as const
const [WIDGET_ZOOM_LEVEL] = allowedWidgets

interface LocationItem {
  coordinates?: string
  enrichedListings?: any[]
  count?: number
  originalLocation?: string
  hostSiteId?: string
  hostName?: string
  listingAddressEnriched?: string
  listingId?: string
  [key: string]: any
}

interface LocationList {
  list: (string | LocationItem)[]
  listColor: string
}

interface MapProps {
  baseUrl: string
  zoomDelta?: number
  widgets?: string[]
  locations?: LocationList[]
  onZoom?: (params: { top_left: L.LatLng; bottom_right: L.LatLng; precision: number }) => void
  pointsMode?: boolean
  platform?: 'saas' | 'prem'
  clickOnMarker?: (hostSiteId: string) => void
}

interface MapState {
  currentZoom: number | null
}

@autobind
class Map extends Component<MapProps, MapState> {
  private map: L.Map | null = null
  private markersOnMap: L.Marker[] = []
  private mapId: string
  private didFitBounds: boolean = false

  constructor(props: MapProps) {
    super(props)

    this.mapId = Math.floor(Math.random() * 100).toString()

    this.state = {
      currentZoom: null,
    }
  }

  componentDidMount() {
    const { baseUrl, zoomDelta = 1, platform = 'saas' } = this.props

    const zoom = 4
    const maxZoom = 9
    const maxNativeZoom = 19
    const bounds = new L.LatLngBounds(new L.LatLng(49.5, -11.3), new L.LatLng(61.2, 2.5))

    this.map = L.map(this.mapId, {
      center: bounds.getCenter(),
      zoom,
      zoomControl: false,
      minBounds: bounds,
      maxBoundsViscosity: 1.0,
      zoomSnap: zoomDelta,
      zoomDelta,
      minZoom: 2,
      renderer: L.canvas(),
      worldCopyJump: true,
    })

    L.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(this.map)

    const tileLayersURL = `${baseUrl}${platform === 'prem' ? '/tile' : ''}/{z}/{x}/{y}.png`

    L.tileLayer(tileLayersURL, {
      detectRetina: true,
      maxZoom,
      maxNativeZoom,
      opacity: 0.5,
    }).addTo(this.map)

    this.map.on('zoomend', async () => {
      if (!this.map) return
      const currentZoom = this.map.getZoom()
      const { onZoom = () => {}, pointsMode = true } = this.props
      const { currentZoom: stateZoom } = this.state
      if (!stateZoom || stateZoom === currentZoom) {
        this.setState({ currentZoom })
        return
      }
      if (!pointsMode) {
        const bounds = this.map.getBounds()
        const top_left = bounds.getNorthWest()
        const bottom_right = bounds.getSouthEast()
        this.markersOnMap.forEach(marker => this.map!.removeLayer(marker))
        this.markersOnMap.splice(0, this.markersOnMap.length)
        onZoom({ top_left, bottom_right, precision: currentZoom })
      }

      this.setState({ currentZoom })
    })

    // this.map.on('dragend', async e => {
    //   const currentZoom = this.map.getZoom();
    //   const { onZoom = () => {}, pointsMode = true } = this.props;
    //   if (!pointsMode) {
    //     const bounds = this.map.getBounds();
    //     const top_left = bounds.getNorthEast().wrap();
    //     const bottom_right = bounds.getSouthWest().wrap();
    //     this.markersOnMap.forEach(marker => this.map.removeLayer(marker));
    //     this.markersOnMap.splice(0, this.markersOnMap.length);
    //     onZoom({ top_left, bottom_right, precision: currentZoom });
    //   }
    // });

    this.didFitBounds = false
  }

  componentDidUpdate(prevProps: MapProps) {
    if (!this.map) return
    this.map.invalidateSize()

    const { locations, pointsMode = true } = this.props

    if (
      prevProps.locations === locations ||
      (pointsMode && _.isEqual(prevProps.locations, locations) && this.markersOnMap.length > 0) ||
      (locations && locations[0]?.list && locations.reduce((acc, value = { list: [] }) => acc + (value.list?.length || 0), 0) === 0)
    ) {
      this.renderMarkers()
      return
    }

    if ((!this.didFitBounds && locations && locations[0]?.list) || pointsMode) {
      const points = this.allLocations.map(item => L.point(item as [number, number]))

      if (L.bounds(points) && L.bounds(points).max) {
        const bounds = [
          [L.bounds(points).max.x, L.bounds(points).max.y],
          [L.bounds(points).min.x, L.bounds(points).min.y],
        ] as [[number, number], [number, number]]
        if (!pointsMode)
          this.map.fitBounds(
            [
              [90, -180],
              [-90, 180],
            ] as [[number, number], [number, number]],
            { maxZoom: 2 },
          )
        else this.map.fitBounds(bounds)
      }
      this.didFitBounds = true
      if (!pointsMode) return
    }

    this.renderMarkers()
  }

  componentWillUnmount() {
    this.didFitBounds = false
    if (this.map) {
      this.markersOnMap.forEach(marker => this.map!.removeLayer(marker))
    }
    this.markersOnMap.splice(0, this.markersOnMap.length)
  }

  filterPoints = (item: string | LocationItem | undefined): boolean =>
    !!item &&
    ((typeof item === 'string' && !!item.length) ||
      (!!(item as LocationItem).coordinates && !!(item as LocationItem).coordinates!.length) ||
      !!(item as LocationItem).enrichedListings)

  convertToPoint = (item: string | LocationItem): [number, number] | string[] => {
    if (typeof item === 'string') {
      return item.split(',') as [number, number]
    }
    if ((item as LocationItem).enrichedListings) {
      return (item as LocationItem).enrichedListings as any
    }
    return (item as LocationItem).coordinates!.split(',') as [number, number]
  }

  async updateView() {
    if (!this.map) return
    const currentZoom = this.map.getZoom()
    const { onZoom = () => {}, pointsMode = true } = this.props
    if (pointsMode) return
    const bounds = this.map.getBounds()
    const top_left = bounds.getNorthWest()
    const bottom_right = bounds.getSouthEast()
    this.markersOnMap.forEach(marker => this.map!.removeLayer(marker))
    this.markersOnMap.splice(0, this.markersOnMap.length)
    onZoom({ top_left, bottom_right, precision: currentZoom })
  }

  get allLocations(): [number, number][] {
    const { locations = [] } = this.props
    if (!locations) return []
    const locationsList = locations
      .map(({ list }) => list && list.filter(this.filterPoints).map(this.convertToPoint))
      .flat() as [number, number][]
    return locationsList
  }

  get locationsByType() {
    const { locations = [] } = this.props

    return locations.map(({ list, listColor }) => {
      const markers = list.filter(this.filterPoints)

      return {
        color: listColor,
        markers,
      }
    })
  }

  render() {
    const { widgets = [], pointsMode = true } = this.props
    const { currentZoom } = this.state

    const shouldDisplayButton = !pointsMode
    // @TODO: Refactor
    const formattedZoomLevel = currentZoom ? 100 + currentZoom * 10 : 100

    const withZoomLevel = widgets.includes(WIDGET_ZOOM_LEVEL)

    return (
      <div
        className={cx(style.container, {
          [style.withZoomLevel]: withZoomLevel,
        })}
      >
        <div id={this.mapId} className={style.map}>
          {shouldDisplayButton && (
            <Button buttonType='secondary-icon' className={cx(style.refresh)} onClick={() => this.updateView()}>
              <Icon name='reload' size='regular'>
                <span className={cx(style.text)}>Search this area</span>
              </Icon>
            </Button>
          )}
        </div>
        {withZoomLevel && <div className={style.zoomLevel}>{formattedZoomLevel}%</div>}
      </div>
    )
  }

  renderMarkers() {
    if (!this.map) return
    this.markersOnMap.forEach(marker => this.map!.removeLayer(marker))
    this.markersOnMap.splice(0, this.markersOnMap.length)

    this.markersOnMap = this.locationsByType
      .map(({ color, markers }) => this.renderMarkersByType(markers, color))
      .flat()
  }

  getRadius = (marker: string | LocationItem, zoom: number): number => {
    let min = 6
    if (zoom >= 12) min = 4 + 2 * (zoom - 12)
    if (typeof marker === 'string' || !(marker as LocationItem).count) return min
    const max = 20
    const candidate = min + (marker as LocationItem).count! / 100
    if (candidate > max) return max
    if (candidate < min) return min
    return candidate
  }

  getPoint(childCount: number): L.Point {
    // if (childCount === 1) {
    //   return new L.Point(10, 10);
    // }
    if (childCount < 10) {
      return new L.Point(25, 25)
    }
    if (childCount < 100) {
      return new L.Point(32, 32)
    }
    if (childCount < 1000) {
      return new L.Point(39, 39)
    }
    if (childCount < 10000) {
      return new L.Point(46, 46)
    }
    if (childCount < 100000) {
      return new L.Point(53, 53)
    }
    return new L.Point(60, 60)
  }

  renderMarkersByType = (markers: (string | LocationItem)[], color: string): L.Marker[] => {
    if (!this.map) return []
    const renderer = L.canvas()
    const zoom = this.map.getZoom()
    const { pointsMode = true, clickOnMarker } = this.props
    const relevantMarkers = markers.slice(0, 1000) // TODO - REMOVE ME
    return relevantMarkers.map(marker => {
      let myIcon: L.DivIcon
      if (!pointsMode && zoom < 14) {
        const childCount = (marker as LocationItem).count || 1
        let c = ' marker-cluster-'
        // if (childCount === 1) {
        //   c += 'single';
        if (childCount < 10) {
          c += 'single'
        } else if (childCount < 100) {
          c += 'tens'
        } else if (childCount < 1000) {
          c += 'hundreds'
        } else if (childCount < 10000) {
          c += 'thousands'
        } else if (childCount < 100000) {
          c += 'tens-thousands'
        } else {
          c += 'huge'
        }

        // const myIcon = L.divIcon({
        //   html: `<div class="custom-marker" style="background-color:${color}"><span></span></div>`,
        //   // html:'<div class="map-label '+instanceclass+'"><div class="map-label-content">'+icontext+'</div><div class="map-label-arrow"></div></div>
        //   className: '',
        //   iconSize: null
        myIcon = new L.DivIcon({
          html: `<div><span>${childCount}</span></div>`,
          className: `marker-cluster${c}`,
          iconSize: this.getPoint(childCount),
        })
      } else {
        myIcon = new L.DivIcon({
          html: `<div class="custom-marker" style="background-color:${color}"><span></span></div>`,
          className: '',
          iconSize: null,
        })
      }
      const point = this.convertToPoint(marker)
      const newMarker = L.marker(point as [number, number], { icon: myIcon })
      if (typeof marker !== 'string') {
        if (marker.originalLocation) {
          newMarker.bindTooltip(`${marker.originalLocation}: ${marker.count}`)
        } else if (marker.hostSiteId) {
          newMarker.bindTooltip(
            `<div> <img src=${require('./assets/user.png')} height="20rem"/> ${marker.hostName}, Host ID: ${
              marker.hostSiteId
            } <br/> &nbsp<img src=${require('./assets/pin.jpg')} height="20rem"/> ${
              marker.listingAddressEnriched
            } <div>`,
          )
          newMarker.on('click', () => {
            if (clickOnMarker && marker.hostSiteId) {
              clickOnMarker(marker.hostSiteId)
            }
          })
        } else if (marker.listingAddressEnriched) {
          newMarker.bindTooltip(
            `<div> Listing ID: ${marker.listingId} <br/> <img src=${require('./assets/pin.jpg')} height="20rem"/> ${
              marker.listingAddressEnriched
            } <div>`,
          )
        } else if (marker.count) {
          // newMarker.bindTooltip(marker.count.toString(), {
          //   permanent: true,
          //   direction: 'center',
          //   className: 'text'
          // });
        }
      }

      newMarker.addTo(this.map)
      return newMarker
    })
  }
}

export default Map
